// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./Assemblies.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import TanStackTable from "../Misc/Table/TanStackTable";
import J2NoResultsFound from "../Misc/J2NoResultsFound/J2NoResultsFound";
import AssembliesConsoleEditAdd from "../Admin/AssembliesConsole/AssembliesConsoleEditAdd/AssembliesConsoleEditAdd";

// CONSTANTS
import { checkboxSort, defaultSort, splitSort, notApplicable } from "../Misc/Table/TanStackTableConstants";

// FUNCTIONAL COMPONENT
const Assemblies = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	const [assemblies, setAssemblies] = useState([]);
	const [assemblyTypes, setAssemblyTypes] = useState([]);
	const [details, setDetails] = useState([]);
	const [groupings, setGroupings] = useState([]);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// Dialogs
	const [editAddOpen, setEditAddOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState(null);

	// USE EFFECT
	useEffect(() => {
		fetchData();
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Assemblies",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the Assemblies page",
		});
	}, []);

	useEffect(() => {
		if (assemblies.length > 0) {
			buildColumns();
			buildRows();
		}
	}, [assemblies, assemblyTypes, details, groupings]);

	// API FUNCTIONS
	const fetchData = async () => {
		setIsLoading(true);
		try {
			const [assembliesRes, assemblyTypesRes, detailsRes, groupingsRes] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/encompass/assemblies/allWithAssociations"),
				getAPICall(instance, accounts[0], "/api/encompass/assemblyTypes/all"),
				getAPICall(instance, accounts[0], "/api/encompass/details/all"),
				getAPICall(instance, accounts[0], "/api/encompass/groupings/all"),
			]);
			setAssemblies(assembliesRes.data);
			setAssemblyTypes(assemblyTypesRes.data || []);
			setDetails(detailsRes.data);
			setGroupings(groupingsRes.data);
		} catch (err) {
			console.error("Error fetching assemblies data:", err);
		}
		setIsLoading(false);
	};

	// HELPER FUNCTIONS
	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "name",
				header: () => <span>Name</span>,
				label: "Name",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "assembly_type_name",
				header: () => <span>Assembly Type</span>,
				label: "Assembly Type",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "checkbox",
				filterOptions: assemblyTypes.map((t) => ({
					id: t.id,
					label: t.name,
				})),
				filterFn: checkboxSort,
			},
			{
				accessorKey: "details",
				header: () => <span>Details</span>,
				label: "Details",
				cell: ({ row }) => {
					const count = row.original.details
						? row.original.details.split("_SPLIT_").filter(Boolean).length
						: 0;
					if (count === 0) return <span style={{ color: "gray", fontStyle: "italic" }}>None</span>;
					return <span>{count} Associated {count === 1 ? "Detail" : "Details"}</span>;
				},
				filterType: "checkbox",
				filterOptions: details.map((d) => ({
					id: d.id,
					label: d.title,
				})),
				filterFn: splitSort,
			},
			{
				accessorKey: "groupings",
				header: () => <span>Groupings</span>,
				label: "Groupings",
				cell: ({ row }) => {
					const count = row.original.groupings
						? row.original.groupings.split("_SPLIT_").filter(Boolean).length
						: 0;
					if (count === 0) return <span style={{ color: "gray", fontStyle: "italic" }}>None</span>;
					return <span>{count} Associated {count === 1 ? "Grouping" : "Groupings"}</span>;
				},
				filterType: "checkbox",
				filterOptions: groupings.map((g) => ({
					id: g.id,
					label: g.name,
				})),
				filterFn: splitSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => onEditClick(row.original.record)}
						sx={{ justifyContent: "flex-end" }}
						disableRipple={true}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const buildRows = () => {
		setRows(
			assemblies.map((a) => ({
				id: a.id,
				name: a.name,
				assembly_type_name: a.assembly_type_name ?? null,
				details: a.split_details,
				groupings: a.split_groupings,
				edit: "",
				record: a,
			}))
		);
	};

	// Dialogs
	const onAddClick = () => {
		setEditing(false);
		setRecordToEdit(null);
		setEditAddOpen(true);
	};

	const onEditClick = (rec) => {
		setEditing(true);
		setRecordToEdit(rec);
		setEditAddOpen(true);
	};

	const onDialogClose = (refresh) => {
		setEditAddOpen(false);
		setRecordToEdit(null);
		setEditing(false);
		if (refresh) fetchData();
	};

	// RENDER
	return (
		<div className="Assemblies">
			<div className="assembliesHeader">
				<div className="assembliesHeaderTitle">
					Assemblies
					<div className="assembliesHeaderButtons">
						<Button
							variant="contained"
							color="primary"
							onClick={onAddClick}
							disabled={isLoading}
						>
							New Record
						</Button>
					</div>
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{assemblies.length === 0 ? (
						<J2NoResultsFound />
					) : (
						<TanStackTable
							columnData={columns}
							rowData={rows}
							defaultSort={[{ id: "name", desc: false }]}
							showSearch={true}
						/>
					)}
				</div>
			)}
			<AssembliesConsoleEditAdd
				open={editAddOpen}
				editing={editing}
				recordToEdit={recordToEdit}
				onClose={onDialogClose}
			/>
		</div>
	);
};

export default Assemblies;
